import React, { createContext, useContext, useState, useEffect } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api'

interface Usuario {
  id: string
  nome: string
  email: string
  perfil: string
  empresa: string | null
  permissoes?: string[] | null
  cliente_id: string | null
}

interface AuthContextType {
  usuario: Usuario | null
  token: string | null
  login: (email: string, senha: string) => Promise<void>
  logout: () => void
  acessarComoCliente: (clienteId: string, usuarioId: string) => Promise<void>
  acessarComoUsuario: (usuarioId: string) => Promise<void>
  voltarAoMeuUsuario: () => void
  acessoComo: { admin_nome: string; alvo_nome: string; empresa: string | null } | null
  clienteAcessado: string | null
  loading: boolean
  primeiroAcesso: boolean
  setPrimeiroAcesso: (v:boolean) => void
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [usuario, setUsuario]           = useState<Usuario | null>(null)
  const [token, setToken]               = useState<string | null>(null)
  const [clienteAcessado, setClienteAcessado] = useState<string | null>(null)
  const [loading, setLoading]           = useState(true)
  const [primeiroAcesso, setPrimeiroAcesso] = useState(false)
  const [acessoComo] = useState<{ admin_nome: string; alvo_nome: string; empresa: string | null } | null>(() => {
    try { return JSON.parse(localStorage.getItem('moobi_acesso_como') || 'null') } catch { return null }
  })

  useEffect(() => {
    const t = localStorage.getItem('moobi_token')
    const u = localStorage.getItem('moobi_usuario')
    if (t && u) {
      setToken(t)
      setUsuario(JSON.parse(u))
      axios.defaults.headers.common['Authorization'] = `Bearer ${t}`
    }
    setLoading(false)
  }, [])

  const login = async (email: string, senha: string) => {
    const resp = await axios.post(`${API}/auth/login`, { email, senha })
    const { token: t, usuario: u, primeiro_acesso } = resp.data
    setToken(t)
    setUsuario(u)
    localStorage.setItem('moobi_token', t)
    localStorage.setItem('moobi_usuario', JSON.stringify(u))
    axios.defaults.headers.common['Authorization'] = `Bearer ${t}`
    if (primeiro_acesso) setPrimeiroAcesso(true)
  }

  const logout = () => {
    setToken(null)
    setUsuario(null)
    setClienteAcessado(null)
    localStorage.removeItem('moobi_token')
    localStorage.removeItem('moobi_usuario')
    localStorage.removeItem('moobi_sessao_original')
    localStorage.removeItem('moobi_acesso_como')
    delete axios.defaults.headers.common['Authorization']
  }

  const acessarComoCliente = async (clienteId: string, usuarioId: string) => {
    setClienteAcessado(clienteId)
  }

  // Superadmin entra no sistema como um usuario de cliente (suporte). Guarda a sessao
  // original pra poder voltar sem pedir senha de novo. O acesso temporario expira em 4h.
  const acessarComoUsuario = async (usuarioId: string) => {
    const resp = await axios.post(`${API}/usuarios/${usuarioId}/acessar-como`)
    const { token: t, usuario: u } = resp.data
    localStorage.setItem('moobi_sessao_original', JSON.stringify({ token, usuario }))
    localStorage.setItem('moobi_acesso_como', JSON.stringify({ admin_nome: usuario?.nome, alvo_nome: u.nome, empresa: u.empresa || null }))
    localStorage.setItem('moobi_token', t)
    localStorage.setItem('moobi_usuario', JSON.stringify(u))
    window.location.href = '/'
  }

  const voltarAoMeuUsuario = () => {
    try {
      const original = JSON.parse(localStorage.getItem('moobi_sessao_original') || 'null')
      if (original?.token && original?.usuario) {
        localStorage.setItem('moobi_token', original.token)
        localStorage.setItem('moobi_usuario', JSON.stringify(original.usuario))
      } else {
        localStorage.removeItem('moobi_token')
        localStorage.removeItem('moobi_usuario')
      }
    } catch {
      localStorage.removeItem('moobi_token')
      localStorage.removeItem('moobi_usuario')
    }
    localStorage.removeItem('moobi_sessao_original')
    localStorage.removeItem('moobi_acesso_como')
    window.location.href = '/'
  }


  return (
    <AuthContext.Provider value={{ usuario, token, login, logout, acessarComoCliente, acessarComoUsuario, voltarAoMeuUsuario, acessoComo, clienteAcessado, loading, primeiroAcesso, setPrimeiroAcesso }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
